import React from 'react';
import { inject, observer } from "mobx-react";
import LocalFireDepartmentOutlinedIcon from '@mui/icons-material/LocalFireDepartmentOutlined';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import BalanceIcon from '@mui/icons-material/Balance';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import GreenSubmitButton from '../buttons/greenSubmitButton';

import './ProductCardStyles.css';
import Img from './Img';

// Translations
import _useTranslate from '../hooks/_useTranslate';
import useThemeColor from '../hooks/useThemeColor';

const ProductCard = ({
  image,
  added,
  title,
  description,
  price,
  oldPrice,
  badges,
  soldOut,
  weighted,
  onClick,
  handleClick,
  attributes,
  feathersStore,
  onlyToProduct,
  recipes
}) => {

  const cart = _useTranslate(feathersStore.language, "cart");
  const common = _useTranslate(feathersStore.language, "common");

  const accent = useThemeColor('--color-accent', '#2E7D32');
  const addedColor = useThemeColor('--color-added', '#f37a7e');

  const getPrice = () => {
    const floatPrice = parseFloat(price);
    return floatPrice.toFixed(2);
  };

  const getOldPrice = () => {
    const floatOldPrice = parseFloat(oldPrice);
    return floatOldPrice.toFixed(2);
  };

  const packagePricing = () => {
    return feathersStore.settings?.deliveryPricing && feathersStore?.tableId === 'eshop'
      && !feathersStore.qrReadOnly();
  };

  const isDisabled = soldOut || weighted || 
    (!feathersStore.restaurantOpen && feathersStore?.tableId === 'eshop') || 
    (feathersStore.tableQrOrderingOnly() && (feathersStore.tableId === 'eshop'));

  const getButtonTitle = () => {
    if (soldOut) return cart.soldOut || 'SOLD OUT';
    if (weighted) return common.weighted || "WEIGHT BASED";
    if (onlyToProduct) return cart.select || "VIEW";
    if (added) return cart.addToCart || 'ADDED';
    return cart.add || "ADD";
  };

  const getButtonIcon = () => {
    if (soldOut) return null;
    if (weighted) return <BalanceIcon />;
    if (onlyToProduct) return <ArrowForwardIcon />;
    if (added) return <CheckCircleIcon />;
    return <ShoppingCartIcon />;
  };

  const getButtonVariant = () => {
    if (soldOut) return "outlined";
    if (weighted) return "outlined";
    if (!feathersStore.restaurantOpen && feathersStore?.tableId === 'eshop') return "outlined";
    if (added) return "contained";
    return "contained";
  };

  const getButtonColor = () => {
    if (soldOut) return "#999";
    if (weighted) return accent; /* theme accent for weight based */
    if (!feathersStore.restaurantOpen && feathersStore?.tableId === 'eshop') return "#999";
    if (added) return addedColor;
    return accent;
  };

  const handleButtonClick = (e) => {
    e.stopPropagation();
    if (isDisabled) return;
    if (onlyToProduct) {
      handleClick && handleClick();
    } else {
      onClick && onClick();
    }
  };

  // Check if image exists
  const hasImage = image && image !== '' && image !== 'null' && image !== 'undefined';

  // Check if there are many attributes
  const hasManyAttributes = attributes && attributes.length > 3;

  return (
    <div className="bfood-product-card">
      {/* Image Section */}
      <div
        className="bfood-card__image-wrapper"
        onClick={handleClick}
        role="button"
        tabIndex={0}
        aria-label={title}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleClick && handleClick(); } }}
      >
        {hasImage ? (
          <Img className="bfood-card__image" src={image} alt="" />
        ) : (
          <div className="bfood-card__image bfood-card__image--no-image" />
        )}
        
        {/* Badges */}
        {badges && badges.length > 0 && (
          <div className="bfood-card__badges">
            {badges.includes('new') && (
              <span className="bfood-badge bfood-badge--new">{cart.new || 'NEO'}</span>
            )}
            {badges.includes('sale') && (
              <span className="bfood-badge bfood-badge--sale">{cart.sale || '-7%'}</span>
            )}
            {badges.includes('hot') && (
              <span className="bfood-badge bfood-badge--hot">
                <LocalFireDepartmentOutlinedIcon />
              </span>
            )}
          </div>
        )}

        {/* Sold Out Overlay */}
        {soldOut && (
          <div className="bfood-card__soldout">
            <span>{cart.soldOut || 'SOLD OUT'}</span>
          </div>
        )}

        {/* Weight Based Indicator */}
        {weighted && !soldOut && (
          <div className="bfood-card__weight-badge">
            <span>⚖️ WEIGHT</span>
          </div>
        )}
      </div>

      {/* Content Section */}
      <div className="bfood-card__content">
        {/* Title */}
        <div className="bfood-card__title-wrapper">
          <div
            className="bfood-card__title"
            onClick={handleClick}
          >
            {title}
          </div>
        </div>

        {/* Short description (used by category listings via the unified card) */}
        {description && (
          <p className="bfood-card__desc">{description}</p>
        )}

        {/* Grouped product composition */}
        {recipes?.some(r => r.isProduct) && (
          <div className="bfood-card__composition">
            {recipes.filter(r => r.isProduct).map(r =>
              `${r.name} ${r.unit === 'gr' ? `${r.quantity}gr` : r.unit === 'Piece' ? `×${r.quantity}` : `${r.quantity} ${r.unit}`}`
            ).join(', ')}
          </div>
        )}

        {/* Attributes */}
        {attributes?.length > 0 && (
          <div 
            className="bfood-card__attributes"
            style={{
              maxHeight: hasManyAttributes ? '120px' : 'auto',
            }}
          >
            {attributes.map((attribute, index) => (
              <div key={index} className="bfood-attribute-item">
                <span className="bfood-attribute-name">
                  {feathersStore._translateTitle(attribute)}
                </span>
                <span className="bfood-attribute-value">
                  {feathersStore._translateNameAttr(attribute)}
                </span>
                <span className="bfood-attribute-price">
                  {feathersStore.formatMoney(attribute[packagePricing() ? 'packagePrice' : 'price'] || '0')}
                </span>
                <span className="bfood-required-star">*</span>
              </div>
            ))}
          </div>
        )}

        {/* Price Section */}
        <div className="bfood-card__prices">
          <span className="bfood-current-price">
            {feathersStore.formatMoney(getPrice())}
          </span>
          {oldPrice && (
            <span className="bfood-old-price">
              <del>{feathersStore.formatMoney(getOldPrice())}</del>
            </span>
          )}
        </div>

        {/* Action Button - MATCHING CATEGORY CARD HEIGHT */}
        {!feathersStore.qrReadOnly() && (
          <div className="bfood-card__action">
            <GreenSubmitButton
              width="100%"
              size="small" /* Keep size small to match category card */
              borderRadius={22} /* pill-style CTA (modern pass) */
              marginLeft={0}
              marginRight={0}
              marginBottom={0}
              marginTop={0.5} /* Small top margin like category card */
              disabled={isDisabled}
              title={getButtonTitle()}
              onClick={handleButtonClick}
              variant={getButtonVariant()}
              color={getButtonColor()}
              textTransform="uppercase"
              fontSize="13px"
              height="36px" /* Match category card button height (small size) */
              elevation={isDisabled ? 0 : 1}
              startIcon={getButtonIcon()}
              fullWidth={true}
              sx={{
                '& .MuiButton-startIcon': {
                  marginRight: '6px',
                  marginLeft: '0',
                },
                '& svg': {
                  fontSize: '18px',
                },
                /* Match category card button styling */
                fontWeight: 600,
                letterSpacing: '0.3px',
                /* Ensure button fills space */
                '&.MuiButton-root': {
                  minWidth: '100%',
                  width: '100%',
                },
                /* Style for outlined buttons (weight based) */
                ...(weighted && !soldOut && {
                  borderColor: accent,
                  color: accent,
                  borderWidth: '2px',
                  '&:hover': {
                    borderColor: accent,
                    color: accent,
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    borderWidth: '2px',
                    filter: 'brightness(0.9)',
                  },
                }),
                /* Style for sold out */
                ...(soldOut && {
                  borderColor: 'var(--border-color)',
                  color: '#999',
                  '&.Mui-disabled': {
                    borderColor: 'var(--border-color)',
                    color: '#999',
                  },
                }),
                /* Style for view button */
                ...(onlyToProduct && !soldOut && !weighted && !isDisabled && {
                  backgroundColor: accent,
                  '&:hover': {
                    backgroundColor: accent,
                    filter: 'brightness(0.9)',
                  },
                }),
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default inject('feathersStore')(observer(ProductCard));